import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, Plus, Tag as TagIcon, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fmtNumber } from '@/shared/lib/format'
import type { TagVocabularyResponse } from '@/shared/api/types'
import { explain } from '@/shared/api/problem'
import { listTagVocabulary } from './api'

/**
 * Tag vocabulary UI.
 *
 * ## Why a picker and not a text box
 *
 * `voter_tag.tag` is a plain `varchar`, not `citext`, so every comparison
 * against it is case-sensitive and exact. The six tag-based targeting presets
 * ask for the literal strings `boda`, `mama_mboga`, `trader`, `farmer`,
 * `pastoralist`, `casual`, `hawker`, `driver`, `student`, `pwd`. A free-text box
 * produces `boda_rider`, `bodaboda` and `boda_operator` inside a week, and every
 * one of those is a voter who has been tagged and will never be targeted —
 * strictly worse than an untagged voter, because somebody believes the work is
 * done.
 *
 * Canonicalisation collapses SPELLING. Only a controlled vocabulary collapses
 * SYNONYMS. So the picker offers what exists, shows how many voters carry it,
 * and makes creating a new one a deliberate, visible act.
 */

// ── the canonical form, previewed ───────────────────────────────────────────

/**
 * A client-side mirror of the server's `TagName.canonical`, **for preview only**.
 *
 * The server is the authority and this is never sent anywhere — `addTag` posts
 * what the user typed and the server canonicalises it. What this buys is that
 * the transformation is VISIBLE before the click: type "Boda Boda" and the field
 * says `→ boda_boda` underneath it.
 *
 * That matters more than it looks. The entire preset outage came from a silent
 * transformation — one layer lowercased, another did not, and nothing on any
 * screen showed either. A user who can see `boda_boda` appear can also see that
 * it is not the `boda` the preset wants, which is a question they can ask.
 */
/**
 * Java's `String.trim()` strips characters <= U+0020, and Java's `\s` is
 * `[ \t\n\x0B\f\r]` unless `UNICODE_CHARACTER_CLASS` is set — it is not.
 * JavaScript's `.trim()` and `\s` are both Unicode-aware.
 *
 * That difference is not academic: a non-breaking space is what you get pasting
 * "Boda Boda" out of Word, Excel or a PDF, which is how half the tags in a
 * campaign will arrive. A Unicode-aware client would print "stored as
 * boda_boda", the server would leave the NBSP in place, and the value would then
 * fail `[a-z0-9_]+` — a confident preview followed by a 400, which is the exact
 * silent-transformation failure this module exists to prevent.
 *
 * So the client mirrors JAVA's whitespace, not JavaScript's.
 */
const JAVA_SPACE_OR_DASH = /(?:[\u0020\u0009\u000a\u000b\u000c\u000d]|-)+/g
const JAVA_TRIM = /^[\u0000-\u0020]+|[\u0000-\u0020]+$/g

export function canonicalPreview(raw: string): string {
  return raw
    .replace(JAVA_TRIM, '')
    .toLowerCase()
    .replace(JAVA_SPACE_OR_DASH, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '')
}

/** The raw value the server length-checks, trimmed the way Java trims. */
export function javaTrim(raw: string): string {
  return raw.replace(JAVA_TRIM, '')
}

/**
 * Names the character rather than printing it.
 *
 * A non-breaking space is invisible, so "remove  " is a message the reader cannot
 * act on — and an NBSP is the single most likely offender, because it is what a
 * paste out of Word or a PDF puts between two words.
 */
const CHAR_NAMES: Record<string, string> = {
  '\u00a0': 'a non-breaking space (paste it as plain text)',
  '\u2009': 'a thin space',
  '\u200b': 'a zero-width space',
  '\u2013': 'an en dash',
  '\u2014': 'an em dash',
}

function describeChar(c: string): string {
  if (CHAR_NAMES[c]) return CHAR_NAMES[c]!
  if (/[\u0021-\u007e]/.test(c)) return c
  return `U+${c.codePointAt(0)!.toString(16).toUpperCase().padStart(4, '0')}`
}

/** What the server will refuse, said before the round trip. */
export function canonicalProblem(raw: string): string | null {
  const trimmed = javaTrim(raw)
  const t = canonicalPreview(raw)
  if (!trimmed) return 'A tag cannot be empty.'
  if (!t) return 'A tag needs at least one letter or digit.'
  // `@Size(max = 64)` on the server is on the RAW value and its own length check
  // is on the canonical one. Both have to pass: checking only the canonical form
  // let a 70-character string that collapses to `a_b` through to a 400.
  if (trimmed.length > 64) return `That is ${trimmed.length} characters; the limit is 64.`
  if (t.length > 64) return `That becomes ${t.length} characters; the limit is 64.`
  if (!/^[a-z0-9_]+$/.test(t)) {
    // `[...t]`, not `.split('')` — splitting by '' cuts surrogate pairs in half, so
    // an emoji produced a message telling the user to remove two lone surrogates.
    const bad = [...new Set([...t].filter((c) => !/[a-z0-9_]/.test(c)))]
      .map(describeChar)
      .join(' ')
    return `Only letters, digits and underscores are allowed — remove ${bad}.`
  }
  return null
}

// ── the chip ────────────────────────────────────────────────────────────────

/**
 * One tag, as a chip.
 *
 * It shows the DISPLAY name and carries the canonical form in the title, because
 * the canonical form is what a targeting rule matches and the display name is
 * not. Anyone debugging "why doesn't this preset find them" needs the string the
 * database holds, and it is one hover away.
 */
export function TagChip({
  tag, displayName, count, onRemove, muted, className,
}: {
  tag: string
  displayName?: string | null
  count?: number
  onRemove?: () => void
  muted?: boolean
  className?: string
}) {
  const label = displayName?.trim() || tag
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-erp-xs',
        muted
          ? 'border-dashed border-border text-muted-foreground'
          : 'border-chocolate/40 bg-chocolate-soft/50 text-chocolate',
        className,
      )}
      title={label === tag ? tag : `${label} · stored as "${tag}"`}
    >
      <TagIcon className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
      <span className="truncate">{label}</span>
      {count !== undefined && (
        <span className="tabular-nums opacity-70">{fmtNumber(count)}</span>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove() }}
          className="opacity-60 hover:opacity-100"
          aria-label={`Remove ${label}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  )
}

// ── the vocabulary, loaded once per mount ───────────────────────────────────

export function useTagVocabulary(opts: { activeOnly?: boolean } = {}) {
  // `null` while loading, `null` on failure — NEVER `[]`. Collapsing a failed load
  // into an empty array made every caller render "no tags exist yet, create the
  // ones your presets need", which is an instruction that is wrong and that most
  // readers cannot act on anyway.
  const [tags, setTags] = useState<TagVocabularyResponse[] | null>(null)
  const [truncated, setTruncated] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let alive = true
    listTagVocabulary(opts.activeOnly ? { active: true } : {})
      .then((r) => { if (alive) { setTags(r.tags); setTruncated(r.truncated); setError(null) } })
      .catch((e) => { if (alive) { setTags(null); setError(explain(e)) } })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, opts.activeOnly])

  return { tags, truncated, error, reload: () => setTick((t) => t + 1) }
}

// ── the picker ──────────────────────────────────────────────────────────────

/**
 * Pick an existing tag, or deliberately create a new one.
 *
 * Three things this does that a plain combobox does not:
 *
 * 1. **Every option carries its voter count.** "Persons with disabilities · 0"
 *    tells the operator, at the moment of choosing, that the preset built on
 *    this tag currently matches nobody. That is the exact fact the programme
 *    rule builder is reporting from the other end.
 * 2. **Creating is a separate, labelled action**, showing the canonical form the
 *    new tag will take. It is never the accidental result of typing and pressing
 *    enter on a near-miss.
 * 3. **A typed term that canonicalises onto an existing tag matches it.** Typing
 *    "Boda Boda" surfaces the existing `boda_boda` rather than offering to
 *    create a duplicate the server would 409 on anyway.
 */
export function TagPicker({
  vocabulary, exclude = [], onPick, onCreate, placeholder = 'Search tags…', autoFocus,
}: {
  vocabulary: TagVocabularyResponse[]
  /** Canonical forms already on this subject — shown as "already applied", not hidden. */
  exclude?: string[]
  onPick: (tag: TagVocabularyResponse) => void
  /** Absent when the caller may not create tags. The create row then does not appear. */
  onCreate?: (raw: string) => void
  placeholder?: string
  autoFocus?: boolean
}) {
  const [term, setTerm] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const canonical = canonicalPreview(term)
  const problem = term.trim() ? canonicalProblem(term) : null
  const has = useMemo(() => new Set(exclude), [exclude.join(',')])

  useEffect(() => { if (autoFocus) inputRef.current?.focus() }, [autoFocus])

  const matches = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return vocabulary
    return vocabulary.filter((v) =>
      // `canonical` can be EMPTY for a term like "-" or "__", and
      // `"boda".includes("")` is true — so without this guard a term the field is
      // simultaneously reporting as invalid matched the entire vocabulary.
      (canonical !== '' && v.tag.includes(canonical))
      || v.displayName.toLowerCase().includes(t)
      || (v.category ?? '').toLowerCase().includes(t))
  }, [vocabulary, term, canonical])

  const exact = vocabulary.find((v) => v.tag === canonical)
  const canOfferCreate = Boolean(onCreate) && canonical.length > 0 && !exact && !problem

  return (
    <div className="min-w-0">
      <input
        ref={inputRef}
        className="erp-input w-full"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder={placeholder}
        aria-label="Search or create a tag"
      />

      {/* The transformation, before the click. */}
      {term.trim() && (
        <p className={cn('mt-1 text-erp-xs', problem ? 'text-red-700 dark:text-red-300' : 'text-muted-foreground')}>
          {problem ?? (
            <>
              stored as <span className="font-mono">{canonical}</span>
              {exact && ' — this tag already exists'}
            </>
          )}
        </p>
      )}

      <ul className="mt-2 max-h-56 overflow-auto rounded-md border border-border divide-y divide-border/60">
        {canOfferCreate && (
          <li>
            <button
              type="button"
              className="w-full text-left px-2.5 py-2 hover:bg-muted flex items-center gap-2"
              onClick={() => { onCreate!(term); setTerm('') }}
            >
              <Plus className="h-3.5 w-3.5 text-chocolate shrink-0" />
              <span className="text-erp-sm">
                Create <span className="font-medium">{term.trim()}</span>
                <span className="text-muted-foreground"> as <span className="font-mono">{canonical}</span></span>
              </span>
            </button>
          </li>
        )}
        {matches.length === 0 && !canOfferCreate && (
          <li className="px-2.5 py-3 text-erp-xs text-muted-foreground text-center">
            {vocabulary.length === 0
              ? 'No tags exist yet. Create the ones your targeting presets need before the fieldwork starts.'
              : 'No tag matches that.'}
          </li>
        )}
        {matches.map((v) => {
          const already = has.has(v.tag)
          return (
            <li key={v.tag}>
              <button
                type="button"
                // `aria-disabled`, not `disabled`. A `disabled` button leaves the tab
                // order entirely, so a keyboard or screen-reader user never met the
                // rows this component's own comment promises are "shown as already
                // applied, not hidden" — functionally hidden is exactly what they were.
                aria-disabled={already || undefined}
                className={cn(
                  'w-full text-left px-2.5 py-2 flex items-center gap-2',
                  already ? 'opacity-60 cursor-default' : 'hover:bg-muted',
                )}
                onClick={() => { if (!already) { onPick(v); setTerm('') } }}
              >
                <span className="min-w-0 flex-1">
                  <span className="text-erp-sm block truncate">{v.displayName}</span>
                  <span className="text-erp-xs text-muted-foreground font-mono">{v.tag}</span>
                </span>
                {!v.active && (
                  <span className="text-erp-xs text-muted-foreground shrink-0">hidden</span>
                )}
                {/* The count at the moment of choosing. A preset built on a tag
                    nobody carries is the thing this whole feature exists to make
                    visible, and this is the earliest place to show it. */}
                <span className="text-erp-xs tabular-nums text-muted-foreground shrink-0">
                  {fmtNumber(v.voterCount)}
                </span>
                {already && (
                  <span className="inline-flex items-center gap-1 text-erp-xs text-emerald-700 dark:text-emerald-400 shrink-0">
                    <Check className="h-3.5 w-3.5" aria-hidden /> already applied
                  </span>
                )}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
